import React, {
  useReducer,
  useState,
  ChangeEvent,
  MouseEvent,
  Fragment,
  useEffect,
  useRef,
  useCallback,
} from 'react';

import toast from 'react-hot-toast';
import MainPartial from '@/features/products/list/partials/main';
import FilterPartial from '@/features/products/list/partials/filter';

import { useTranslation } from 'next-i18next';
import {
  Pagination,
  Image,
  Button,
  ButtonVariant,
  Icon,
  Input,
  SearchIcon,
  CloseFilledIcon,
  Link,
  CloseIcon,
  XLineIcon,
} from '@/components';
import { debounce, getCurrentPage, getTotalPage } from '@/libs/app';
import { productsQueryBuilder } from '@/features/products/list/util';
import { useFetchProductsQuery } from '@/features/products/list/api';
import { reducer, initialState } from '@/features/products/list/reducer';
import {
  Category,
  ReducerType,
  cursorTypes,
  CursorDefault,
  InitMasterDataTypes,
  ProductInputKeys,
  KeyFilter,
  priceFilter,
  InitMasterDataCustomTypes,
  CategoryDefault,
} from '@/features/products/list/const';
import { useRouter } from 'next/router';
import MetaTag from '@/components/ui/MetaTag';
import { PagePath } from '@/layouts/shared';
import { AnimatePresence, motion } from 'framer-motion';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/scrollbar';
import { Swiper, SwiperSlide } from 'swiper/react';
import { EffectFade, Navigation } from 'swiper';
import { Product } from '@/features/products/shared';
import RelatedProduct from '@/features/products/detail/partials/RelatedProduct';

export type ProductListLayoutProps = {
  categories: Category[];
  category: Category;
  masterData: InitMasterDataTypes;
  masterDataPearl: InitMasterDataCustomTypes;
  url?: string;
};

const ProductListLayout = ({
  categories,
  masterData,
  masterDataPearl,
  category,
}: ProductListLayoutProps) => {
  const { t } = useTranslation(['product', 'layout']);
  const Router = useRouter();
  const [state, dispatch] = useReducer(reducer, initialState);
  const [cursor, setCursor] = useState<cursorTypes>(CursorDefault);
  const [newestProducts, setNewestProducts] = useState<Product[]>([]);

  useEffect(() => {
    if (Router.query.page) {
      dispatch({ type: ReducerType.OnPageChange, payload: +Router.query.page });
    } else {
      dispatch({ type: ReducerType.OnPageChange, payload: 1 });
    }
    if (Router.query.id) {
      let _p = Router.query.id + '';
      dispatch({
        type: ReducerType.OnCategoryChange,
        payload: _p.toUpperCase(),
      });
    } else {
      if (Router.query.tag)
        dispatch({ type: ReducerType.OnCategoryChange, payload: '' });
      else
        dispatch({
          type: ReducerType.OnCategoryChange,
          payload: CategoryDefault,
        });
    }
    if (Router.query.tag) {
      dispatch({
        type: ReducerType.OnTagChange,
        payload: Router.query.tag + '',
      });
    } else {
      dispatch({ type: ReducerType.OnTagChange, payload: '' });
    }

    dispatch({
      type: ReducerType.OnKeywordChange,
      payload: (Router.query.keyword as string) || '',
    });
  }, [Router.query]);
  const { refetch, isLoading } = useFetchProductsQuery(
    productsQueryBuilder(state),
    {
      refetchOnWindowFocus: false,
      enabled: false,
      retry: false,
      onError: ({ message }) => {
        toast.error(message);
      },
      onSuccess: ({ data, total_records: totalRecords }) => {
        const edges = Array.isArray(data) ? data : [];
        setCursor({ edges, count: +totalRecords });
      },
    }
  );
  useEffect(() => {
    refetch();
  }, [refetch, state]);

  const { refetch: refetchNewestProducts } = useFetchProductsQuery(
    { limit: 10, offset: 0, sort_by: 'created_at|desc' },
    {
      refetchOnWindowFocus: false,
      enabled: false,
      retry: false,
      onError: ({ message }) => {},
      onSuccess: ({ data }) => {
        console.log('data: ', data);
        setNewestProducts(Array.isArray(data) ? data : []);
      },
    }
  );

  useEffect(() => {
    console.log('fetch');
    refetchNewestProducts();
  }, [refetchNewestProducts]);

  const onPageChange = (page: number) => {
    // window.scrollTo({ top: 0, left: 0 });
    document
      .getElementById('main-category')
      ?.scrollIntoView({ behavior: 'smooth' });
    Router.push(
      {
        pathname: Router.pathname,
        query: { ...Router.query, page },
      },
      undefined,
      { shallow: true }
    );
    return dispatch({ type: ReducerType.OnPageChange, payload: page });
  };
  const buildShowFilter = (state: any) => {
    let _ = [];
    for (let key of ProductInputKeys) {
      if (state[key])
        _.push({
          key: key,
          value: state[key],
        });
    }
    return _;
  };
  const onKeywordChange = (value: string) => {
    dispatch({ type: ReducerType.OnPageChange, payload: 1 });
    if (value.includes('#')) {
      dispatch({ type: ReducerType.OnKeywordChange, payload: '' });
      dispatch({
        type: ReducerType.OnTagChange,
        payload: value.replace('#', ''),
      });
    } else {
      if (!Router.query.tag) {
        dispatch({ type: ReducerType.OnTagChange, payload: '' });
      }
      dispatch({ type: ReducerType.OnKeywordChange, payload: value });
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps

  const onCategoryChange = (e: MouseEvent<HTMLDivElement>) => {
    const { id } = e.currentTarget.dataset;
    if (id) {
      Router.push({
        pathname: PagePath.CategoryPage,
        query: { id: id.toLocaleLowerCase() },
      });
    }
  };
  const onFilterReset = () => {
    return dispatch({ type: ReducerType.OnFilterReset });
  };
  const removeFilterKey = (key: string) => {
    dispatch({ type: ReducerType.OnPageChange, payload: 1 });
    Router.push(
      { pathname: Router.pathname, query: { ...Router.query, page: 1 } },
      undefined,
      { shallow: true }
    );
    if (key === KeyFilter.Color)
      return dispatch({ type: ReducerType.OnColorChange, payload: '' });
    if (key === KeyFilter.GemMaterial)
      return dispatch({ type: ReducerType.OnGemMaterialChange, payload: '' });
    if (key === KeyFilter.GemType)
      return dispatch({ type: ReducerType.OnGemTypeChange, payload: '' });
    if (key === KeyFilter.GemPure)
      return dispatch({ type: ReducerType.OnGemPureChange, payload: '' });
    if (key === KeyFilter.JewelryMaterial)
      return dispatch({
        type: ReducerType.OnJewelryMaterialChange,
        payload: '',
      });
    if (key === KeyFilter.Price)
      return dispatch({ type: ReducerType.OnPriceChange, payload: '' });
    if (key === KeyFilter.Quotation)
      return dispatch({ type: ReducerType.OnQuotationChange, payload: '' });
    if (key === KeyFilter.SortBy)
      return dispatch({ type: ReducerType.OnSortBy, payload: '' });
  };

  return (
    <Fragment>
      <MetaTag
        title={category?.seo?.title || t('list.title')}
        seo={category?.seo}
      />
      <h1 className="tw-hidden">
        {category?.product_category_name || t('list.title')}
      </h1>
      <h2 className="tw-hidden">
        {category?.product_category_name || t('list.title')}
      </h2>
      <h2 className="tw-hidden">{category?.description || t('list.title')}</h2>
      <h3 className="tw-hidden">{category?.description || t('list.title')}</h3>

      <div
        className={`md:tw-pb-16 ${
          Router.query.tag ? 'md:tw-border-t tw-border-primary-main' : ''
        }`}
      >
        {!Router.query.tag && category?.banner_images && (
          <div className="tw-block tw-relative tw-w-full tw-pb-40-percent md:tw-pb-35-percent">
            <Image
              className="tw-h-full tw-w-full  !tw-absolute tw-left-0 tw-top-0"
              src={category.banner_images.url}
              alt={category.product_category_name}
            />
          </div>
        )}
        <div id="main-category">
          {Router.query.tag ? (
            <div className="tw-pt-4 tw-text-secondary-70 container">
              <Link
                className="!tw-text-secondary-70"
                url={{ pathname: PagePath.ProductsPage }}
              >
                {t('detail.product')}
              </Link>
              {` > #${Router.query.tag}`}
            </div>
          ) : (
            <div>
              {/* <div className="container w-full tw-hidden md:tw-block">
                <Swiper
                  slidesPerView={'auto'}
                  spaceBetween={32}
                  keyboard={{
                    enabled: true,
                  }}
                  pagination={{
                    clickable: true,
                  }}
                  // loop={true}
                  // navigation={false}
                  modules={[EffectFade, Navigation]}
                  className="product-loop-swiper tw-overflow-auto category-scrollbar tw-py-8"
                  id="swiper"
                >
                  {categories.map((item, key) => (
                    <SwiperSlide
                      key={`item-${key}`}
                      className="tw-relative tw-pb-2 !tw-w-max"
                    >
                      <div
                        key={item.code}
                        data-id={item.code}
                        onClick={onCategoryChange}
                        className={`tw-cursor-pointer tw-text-secondary-70 tw-uppercase tw-py-1 tw-flex tw-justify-center tw-items-center ${
                          state.cat_code === item.code
                            ? 'underline-custom tw-border-primary-main tw-font-bold'
                            : ''
                        }`}
                      >
                        <span>{item.product_category_name}</span>
                        {item.code === 'KHUYENMAI' && (
                          <Image
                            src="/sale.gif"
                            alt="sale"
                            className="tw-w-6 tw-h-6 tw-ml-2"
                          />
                        )}
                      </div>
                    </SwiperSlide>
                  ))}
                </Swiper>
              </div> */}
              <div className="tw-bg-white tw-bg-center tw-bg-cover tw-bg-no-repeat tw-py-4 md:tw-hidden">
                <div className="container tw-flex tw-flex-col tw-gap-y-4 tw-px-0">
                  <div className="tw-w-full md:tw-max-w-max tw-flex tw-flex-col">
                    {/* <div className="tw-flex tw-gap-x-8 tw-text-primary-main tw-overflow-auto tw-whitespace-nowrap tw-pb-1 category-scrollbar">
                      {categories.map(({ code, product_category_name }) => {
                        return (
                          <div
                            key={code}
                            data-id={code}
                            onClick={onCategoryChange}
                            className={`tw-cursor-pointer tw-text-secondary-70 tw-uppercase tw-py-1 ${
                              state.cat_code === code
                                ? 'underline-custom tw-border-primary-main tw-font-bold'
                                : ''
                            }`}
                          >
                            {product_category_name}
                          </div>
                        );
                      })}
                    </div> */}
                    <div className="tw-mt-4 tw-block md:tw-hidden">
                      <FilterPartial
                        t={t}
                        categories={categories}
                        masterDataPearl={masterDataPearl}
                        masterData={masterData}
                        state={state}
                        dispatch={dispatch}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
        <div className={`container tw-mx-auto tw-mt-8`}>
          <h1 className="tw-uppercase tw-text-2xl tw-text-secondary-70 tw-font-bold">
            {category?.product_category_name || t('list.title')}
          </h1>
          <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-4 tw-gap-4 md:tw-mt-6">
            <div className="tw-col-span-1 md:tw-col-span-4 tw-flex tw-flex-col tw-gap-y-8">
              <div className="md:tw-grid tw-grid-cols-12 tw-gap-4">
                {/* <div className="tw-relative tw-col-span-12">
                  <Input
                    // variant={InputVariant.Outlined}
                    onChange={(e) => onKeywordChange(e.target.value)}
                    value={state.product_keyword}
                    startAdornment={
                      <Icon
                        className="tw-text-secondary-70"
                        icon={<SearchIcon />}
                      />
                    }
                    placeholder={t('list.filter.keyword')}
                    containerClassName="tw-rounded-5 tw-w-full sm:tw-w-full tw-text-primary-main tw-border tw-border-primary-main tw-py-2 tw-px-3 tw-hidden md:tw-flex tw-h-10"
                    className=" tw-text-secondary-70"
                  />
                  <div className=""></div>
                </div> */}
                <div className="tw-col-span-12">
                  <div className="tw-hidden md:tw-block">
                    <FilterPartial
                      t={t}
                      categories={categories}
                      masterDataPearl={masterDataPearl}
                      masterData={masterData}
                      state={state}
                      dispatch={dispatch}
                    />
                  </div>
                </div>
              </div>
              <AnimatePresence>
                {(!!buildShowFilter(state).length ||
                  state.price ||
                  state.product_keyword) && (
                  <motion.div
                    className="sort tw-grid tw-grid-cols-4 md:tw-grid-cols-5 tw-gap-x-4"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                  >
                    <div className="tw-col-span-1">
                      <Button
                        onClick={onFilterReset}
                        variant={ButtonVariant.Outlined}
                        className="!tw-rounded-5 tw-px-3 tw-py-1 !tw-border-gray-31 tw-text-base tw-w-full tw-text-secondary-70"
                      >
                        {t('list.btn.reset')}
                      </Button>
                    </div>
                    <div className="tw-col-span-3 md:tw-col-span-4 tw-flex tw-flex-wrap tw-gap-x-4 tw-gap-y-2">
                      {buildShowFilter(state).map((item) => {
                        return (
                          <div
                            className="tw-rounded-full tw-px-3 tw-py-1 tw-text-base tw-flex tw-justify-between tw-items-center tw-w-max tw-bg-primary-50 tw-border tw-border-primary-70 hover:tw-border-primary-main tw-text-primary-main"
                            key={item.key}
                          >
                            <span className="tw-mr-3">{item.value}</span>
                            <div
                              className="tw-w-5 tw-h-5 tw-flex tw-justify-center tw-items-center tw-cursor-pointer"
                              onClick={() => removeFilterKey(item.key)}
                            >
                              <Icon
                                className="!tw-w-2 !tw-h-2 tw-text-primary-main tw-flex tw-items-center tw-justify-center"
                                icon={<XLineIcon />}
                              ></Icon>
                            </div>
                          </div>
                        );
                      })}
                      {state.price && (
                        <div
                          className="tw-rounded-full tw-px-3 tw-py-1 tw-text-base tw-flex tw-justify-between tw-items-center tw-w-max tw-bg-primary-50 tw-border tw-border-primary-70 hover:tw-border-primary-main tw-text-primary-main"
                          key="price"
                        >
                          <span className="tw-mr-3">
                            {
                              priceFilter.find((v) => v.value == state.price)
                                ?.text
                            }
                          </span>
                          <div
                            className="tw-w-5 tw-h-5 tw-flex tw-justify-center tw-items-center tw-cursor-pointer"
                            onClick={() => removeFilterKey('price')}
                          >
                            <Icon
                              className="!tw-w-2 !tw-h-2 tw-text-primary-main tw-flex tw-items-center tw-justify-center"
                              icon={<XLineIcon />}
                            ></Icon>
                          </div>
                        </div>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
              {state.product_keyword &&
                (!cursor.edges.length ? (
                  <div className="tw-text-secondary-70">
                    {t('list.no-product')} {`"`}
                    {state.product_keyword}
                    {`"`}
                  </div>
                ) : (
                  <div className="tw-text-secondary-70">
                    {t('list.label.resultSearch')} {`"`}
                    {state.product_keyword}
                    {`"`}
                  </div>
                ))}
              {!Router.query.tag &&
                state.tags &&
                (!cursor.edges.length ? (
                  <div className="tw-text-secondary-70">
                    {t('list.no-product')} {`"`}#{state.tags}
                    {`"`}
                  </div>
                ) : (
                  <div className="tw-text-secondary-70">
                    {t('list.label.resultSearch')} {`"`}#{state.tags}
                    {`"`}
                  </div>
                ))}
              {!state.product_keyword &&
                !!buildShowFilter(state).length &&
                !cursor.edges.length && (
                  <div className="tw-text-secondary-70">
                    {t('list.no-product')}
                  </div>
                )}
              <AnimatePresence>
                {!!cursor.edges.length && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                  >
                    <MainPartial
                      catCode={category.code}
                      t={t}
                      edges={cursor.edges}
                      isLoading={isLoading}
                    />
                  </motion.div>
                )}
              </AnimatePresence>

              <div className="tw-flex tw-justify-end">
                {getTotalPage(cursor.count, state.limit) > 1 && (
                  <Pagination
                    onPageChange={onPageChange}
                    page={getCurrentPage(state.limit, state.offset)}
                    count={getTotalPage(cursor.count, state.limit)}
                  />
                )}
              </div>

              {!cursor.edges.length && !!newestProducts.length && (
                <div className="tw-mt-10 tw-hidden lg:tw-block">
                  <RelatedProduct
                    title={t('list.label.related-products')}
                    cateCode={category.code}
                    products={newestProducts}
                    t={t}
                    isHideSeeMore={true}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default ProductListLayout;
